/*
 * How the workbench reads the operation history and the lock/rate signals.
 *
 * The write lease and the rate counters were once DERIVED here out of the
 * history, because no response carried them. The API now reports both on
 * `GET /limits` — `wiki_write_lease` and `rate_usage` — so this module only
 * interprets those fields. One thing is still local by design: the operation
 * TAXONOMY. `op` and `status` are open strings on the wire, so an unfamiliar
 * value has to render as itself rather than disappear.
 */
import type {
  History,
  HistoryOperation,
  KnowledgeBaseLimits,
  WikiWriteLease,
} from '../../../api/knowledgeBases.ts'

/** Statuses that mean the operation has not reached a terminal state. */
const RUNNING = new Set(['running', 'pending', 'queued', 'in_progress', 'started'])

export function isRunning(op: HistoryOperation): boolean {
  if (RUNNING.has(op.status)) return true
  // A missing `finished_at` on a non-terminal-looking status is the same signal;
  // trusting only the enum would miss a status spelling we have not seen.
  return (op.finished_at ?? null) === null && !isFailed(op) && op.status !== 'completed'
}

export function isFailed(op: HistoryOperation): boolean {
  return op.status === 'failed' || op.status === 'cancelled' || op.status === 'error'
}

export interface Lease {
  held: boolean
  /** True when the run holding it was started from this browser tab. */
  mine: boolean
  /** When the lease lapses on its own, ISO — the deadline worth rendering. */
  expiresAt: string | null
  /** The in-flight history operation matching the holder, when history names one. */
  op: HistoryOperation | null
}

const FREE: Lease = { held: false, mine: false, expiresAt: null, op: null }

/**
 * Who holds the write lease on `wiki/`, as the server reports it.
 *
 * `stale: true` means the holder's TTL has lapsed and the row merely has not been
 * reaped yet — the next operation WILL get the lease, so rendering it as a lock
 * would promise a refusal the server no longer makes. The history is consulted
 * only to put a name on the holder; it never decides whether the lock exists.
 */
export function leaseFrom(
  lease: WikiWriteLease | null | undefined,
  history: History | null,
  myRunId: string | null,
): Lease {
  if (!lease?.held || lease.stale) return FREE
  const holderRun = lease.held_by_run_id ?? null
  const op =
    holderRun === null
      ? null
      : (history?.ops ?? []).find((candidate) => (candidate.run_id ?? null) === holderRun) ?? null
  return {
    held: true,
    mine: holderRun !== null && myRunId !== null && holderRun === myRunId,
    expiresAt: lease.expires_at ?? null,
    op,
  }
}

export interface Rates {
  /** Ingests in the rolling day the server meters, not since local midnight. */
  ingestsToday: number | null
  /** Ingest + query + lint in the rolling 30 days. */
  opsThisMonth: number | null
  /** Live bases the owner holds — the numerator `max_knowledge_bases` meters. */
  knowledgeBases: number | null
  /** Operations running right now across all of the owner's bases. */
  concurrentOps: number | null
}

/**
 * The counted-cap numerators, read off the limits response. The server is the
 * meter — these are the exact counters the quota checks read, over the same
 * rolling windows — so a gauge built from them can never drift from enforcement.
 * `null` means the response carried no counters, and a gauge says so rather than
 * rendering a plausible zero.
 */
export function ratesFrom(limits: KnowledgeBaseLimits | null): Rates {
  const usage = limits?.rate_usage
  if (!usage) {
    return { ingestsToday: null, opsThisMonth: null, knowledgeBases: null, concurrentOps: null }
  }
  return {
    ingestsToday: usage.ingests_last_day ?? 0,
    opsThisMonth: usage.ops_last_30_days ?? 0,
    knowledgeBases: usage.live_knowledge_bases ?? 0,
    concurrentOps: usage.concurrent_ops_running ?? 0,
  }
}

export interface OpLook {
  label: string
  pill: string
  timelineClass: string
}

const OP_LOOKS: Record<string, OpLook> = {
  ingest: { label: 'ingest', pill: 'pill--info', timelineClass: 'tl--ingest' },
  query: { label: 'query', pill: 'pill--info', timelineClass: 'tl--query' },
  lint: { label: 'lint', pill: 'pill--warn', timelineClass: 'tl--lint' },
  schema: { label: 'schema', pill: 'pill--info', timelineClass: '' },
  revert: { label: 'revert', pill: 'pill--neutral', timelineClass: 'tl--revert' },
  init: { label: 'init', pill: 'pill--neutral', timelineClass: '' },
}

export function opLook(op: string): OpLook {
  return OP_LOOKS[op] ?? { label: op, pill: 'pill--neutral', timelineClass: '' }
}

export function statusLook(op: HistoryOperation): { label: string; pill: string } {
  if (isRunning(op)) return { label: 'идёт', pill: 'pill--warn' }
  if (isFailed(op)) return { label: op.status, pill: 'pill--danger' }
  return { label: op.status || 'завершено', pill: 'pill--ok' }
}

/** `👤` for a person, `🤖` for the agent — the badge used across every history view. */
export function authorBadge(author: string | null | undefined): string {
  if (!author) return '·'
  return author === 'agent' || author === 'assistant' ? '🤖' : '👤'
}

export function authorLabel(author: string | null | undefined): string {
  if (!author) return 'неизвестно'
  return author === 'agent' || author === 'assistant' ? 'Агент' : 'Вы'
}

/** Total commits an operation produced, when both ends of the range are known. */
export function commitSummary(op: HistoryOperation): string {
  if (!op.git_commit_first) return 'без коммитов'
  if (!op.git_commit_last || op.git_commit_first === op.git_commit_last) return '1 коммит'
  return `${op.git_commit_first.slice(0, 7)}‥${op.git_commit_last.slice(0, 7)}`
}
